import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Shield, Users, CheckCircle, XCircle } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const founderResponsibilities = [
  "IP and brand enforcement",
  "Governance veto rights",
  "Certification oversight",
  "System architecture decisions",
];

const explicitlyExcluded = [
  "Client delivery",
  "Sales operations",
  "Day-to-day management",
  "Operational decisions",
];

const FounderPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="founder-page">
      <SEOHead 
        title="Founder Role - Post-Exit Governance"
        description="Founder operates as system architect and governance authority, not day-to-day operator. Authority without operations."
        url="https://onpointauthority.com/founder"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12" data-testid="founder-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Management & Governance
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Founder Authority<br />
              <span className="text-primary">Without Operations</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              Post-exit, the founder serves as IP steward and governance authority—not an operator. 
              This role protects brand integrity while eliminating key-person risk.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Responsibilities vs Excluded */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="role-definition-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Responsibilities */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-primary/5 border border-primary/20 p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="h-6 w-6 text-primary" strokeWidth={1.5} />
                <p className="text-xs font-medium tracking-[0.2em] uppercase text-primary">
                  Founder Responsibilities
                </p>
              </div>
              <ul className="space-y-4">
                {founderResponsibilities.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-foreground">
                    <div className="w-2 h-2 bg-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Explicitly Excluded */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="bg-card/5 border border-white/5 p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <XCircle className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
                <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground">
                  Explicitly Excluded
                </p>
              </div>
              <ul className="space-y-4">
                {explicitlyExcluded.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-muted-foreground">
                    <div className="w-2 h-2 bg-white/20" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Case Architecture */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="case-architecture-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              System Proof
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-6">
              Authority Proven<br />
              <span className="text-primary">Through Structure</span>
            </h2>
            <p className="text-lg font-light text-muted-foreground max-w-2xl">
              Buyers underwrite systems. Operators follow structure. Proof lives in architecture, not anecdotes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "Authority Application Intake System", desc: "Documented qualification and screening" },
              { title: "Operator Certification Pipeline", desc: "Training and licensing framework" },
              { title: "Decision Governance Engine", desc: "Approval, hold, and decline logic" },
              { title: "Buyer-Ready Documentation Stack", desc: "Diligence-grade system records" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/5 p-6"
              >
                <h3 className="text-foreground font-medium mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="why-matters-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Strategic Rationale
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-8">
              Why This<br />
              <span className="text-primary">Matters</span>
            </h2>

            <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed mb-10">
              <p>
                This governance structure ensures the business operates with institutional 
                integrity—regardless of founder involvement level.
              </p>
              <p className="text-foreground">
                Buyers acquire a documented, transferable authority engine that reduces 
                key-person risk and increases valuation multiple through governance maturity.
              </p>
            </div>

            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="founder-cta"
              >
                Request Authority Review
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FounderPage;
